import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "app/store";
import { useListGuestsQuery } from "features/ui-content/endpoints";
import {
  combinedOwnerOptionsLoaded,
  litigationCombinedOwnerOptionsLoaded,
  noticeCombinedOwnerOptionsLoaded,
  mapGuests,
  sortMembers,
} from "features/ui-content/slice";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { TeamMember } from "features/ui-content/definitions";

/**
 * Hook to combine guest members with existing team members
 * @param guestMembers Guest members data from API
 * @param existingTeamOwnerOptions Existing team member options
 */
export const useCombinedOptions = (
  guestMembers: Array<TeamMember> | undefined,
  existingTeamOwnerOptions: OptionDefinition[]
) => {
  return useMemo(() => {
    if (!guestMembers) {
      return existingTeamOwnerOptions;
    }

    const guestOptions = mapGuests(guestMembers);
    return sortMembers([...existingTeamOwnerOptions, ...guestOptions]);
  }, [guestMembers, existingTeamOwnerOptions]);
};

/**
 * Hook to load guest members and combine them with existing team members
 * @param resourceId The resource ID to fetch guests for
 */
export const useGuestsLoader = (resourceId: string) => {
  const dispatch = useAppDispatch();

  // Get existing team members from store
  const existingTeamOwnerOptions = useAppSelector(
    (state) => state.uiContent.ownerOptions
  );

  const isDefaultOwners =
    existingTeamOwnerOptions.length === 1 &&
    existingTeamOwnerOptions[0].value === "No owner";

  // Fetch guest members for the current resource
  const { data: guestMembersData, isLoading: isLoadingGuestMembers } =
    useListGuestsQuery(
      { resourceId: resourceId || "" },
      {
        refetchOnMountOrArgChange: true,
        skip: !resourceId,
      }
    );

  const combinedOptions = useCombinedOptions(
    guestMembersData?.members,
    existingTeamOwnerOptions
  );

  useEffect(() => {
    if (!isDefaultOwners && guestMembersData?.members) {
      dispatch(combinedOwnerOptionsLoaded(combinedOptions));
      dispatch(litigationCombinedOwnerOptionsLoaded(combinedOptions));
      dispatch(noticeCombinedOwnerOptionsLoaded(combinedOptions));
    }
  }, [dispatch, combinedOptions, guestMembersData?.members, isDefaultOwners]);

  return {
    isLoadingGuestMembers,
  };
};
